/**
 * Navigation Dropdown Controller
 *
 * Manages desktop navigation dropdowns with:
 * - Click toggle
 * - Full keyboard navigation (Arrow keys, Escape, Tab)
 * - ARIA state management
 * - Only one dropdown open at a time (including language dropdown)
 */
(() => {
	'use strict';

	/** @type {HTMLElement|null} */
	let activeItem = null;

	/**
	 * Check if a menu item's dropdown is currently open.
	 * @param {HTMLElement} li
	 * @returns {boolean}
	 */
	const isOpen = (li) => li.classList.contains('toggle');

	/**
	 * Open a dropdown for a menu item. Closes any other open dropdown first.
	 * @param {HTMLElement} li
	 * @param {'click'|'keyboard'} source
	 */
	const openDropdown = (li, source) => {
		// Close any other open dropdown
		if (activeItem && activeItem !== li) {
			closeDropdown(activeItem);
		}

		const submenu = li.querySelector('.submenu-dropdown, .language-menu');
		const button =
			li.querySelector('.submenu-button') || li.querySelector('#language-menu-button');
		const link = li.querySelector(':scope > a.menu-link');

		if (!submenu) return;

		li.classList.add('toggle');
		submenu.classList.add('toggle');

		if (button) {
			button.classList.add('toggle');
			button.setAttribute('aria-expanded', 'true');
		}

		if (link) {
			link.classList.add('toggle');
		}

		activeItem = li;
	};

	/**
	 * Close a dropdown for a menu item.
	 * @param {HTMLElement} li
	 */
	const closeDropdown = (li) => {
		const submenu = li.querySelector('.submenu-dropdown, .language-menu');
		const button =
			li.querySelector('.submenu-button') || li.querySelector('#language-menu-button');
		const link = li.querySelector(':scope > a.menu-link');

		li.classList.remove('toggle');

		if (submenu) submenu.classList.remove('toggle');

		if (button) {
			button.classList.remove('toggle');
			button.setAttribute('aria-expanded', 'false');
		}

		if (link) {
			link.classList.remove('toggle');
		}

		if (activeItem === li) {
			activeItem = null;
		}
	};

	/**
	 * Close all open dropdowns.
	 */
	const closeAll = () => {
		if (activeItem) {
			closeDropdown(activeItem);
		}
	};

	/**
	 * Get all focusable menu items within a submenu.
	 * @param {HTMLElement} submenu
	 * @returns {HTMLElement[]}
	 */
	const getMenuItems = (submenu) => {
		if (!submenu) return [];

		// Get all links within list items - use simple selector and deduplicate
		const allLinks = submenu.querySelectorAll('li > a');

		// Convert NodeList to Array and deduplicate
		const seen = new Set();
		const uniqueLinks = [];

		allLinks.forEach((link) => {
			if (!seen.has(link)) {
				seen.add(link);
				// Exclude disabled links from keyboard navigation
				if (link.getAttribute('aria-disabled') !== 'true') {
					uniqueLinks.push(link);
				}
			}
		});

		return uniqueLinks;
	};

	/**
	 * Initialize click behavior for a submenu button or language button.
	 * @param {HTMLElement} li
	 * @param {HTMLElement} button
	 */
	const initClick = (li, button) => {
		button.addEventListener('click', (e) => {
			// If the click originated from a submenu link, let it navigate
			const submenuLink = e.target.closest('[role="menuitem"]');
			if (submenuLink && submenuLink !== button) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			if (isOpen(li)) {
				closeDropdown(li);
			} else {
				openDropdown(li, 'click');
			}
		});
	};

	/**
	 * Initialize keyboard navigation for a navigable item.
	 * @param {HTMLElement} li
	 */
	const initKeyboard = (li) => {
		const link = li.querySelector(':scope > a');
		const button =
			li.querySelector('.submenu-button') || li.querySelector('#language-menu-button');
		const submenu =
			li.querySelector('.submenu-dropdown') ||
			li.querySelector('.language-menu') ||
			li.querySelector('#language-menu');

		// Keyboard on the link
		if (link) {
			link.addEventListener('keydown', (e) => {
				handleTopLevelKeydown(e, li, submenu);
			});
		}

		// Keyboard on the submenu button
		if (button) {
			button.addEventListener('keydown', (e) => {
				handleTopLevelKeydown(e, li, submenu);
			});
		}

		// Close once focus genuinely leaves the item - e.g. tabbing past the last
		// submenu link - rather than on the first Tab keypress inside it.
		if (submenu) {
			li.addEventListener('focusout', (e) => {
				if (!isOpen(li)) return;
				if (e.relatedTarget && li.contains(e.relatedTarget)) return;
				closeDropdown(li);
			});
		}

		// Keyboard within submenu items
		if (submenu) {
			// Attach to individual links to ensure events are caught
			const menuLinks = submenu.querySelectorAll('li > a');
			menuLinks.forEach((link) => {
				link.addEventListener('keydown', (e) => {
					handleSubmenuKeydown(e, li, submenu);
				});
			});
		}
	};

	/**
	 * Handle keydown on top-level menu item (link or button).
	 * @param {KeyboardEvent} e
	 * @param {HTMLElement} li
	 * @param {HTMLElement|null} submenu
	 */
	const handleTopLevelKeydown = (e, li, submenu) => {
		// Ignore events bubbling up from within the submenu
		if (submenu && submenu.contains(e.target)) return;

		const siblings = getTopLevelItems();
		const index = siblings.indexOf(li);

		switch (e.key) {
			case 'ArrowDown':
			case 'Down':
				if (submenu) {
					e.preventDefault();
					openDropdown(li, 'keyboard');
					const firstItem = getMenuItems(submenu)[0];
					if (firstItem) firstItem.focus();
				}
				break;

			case 'ArrowUp':
			case 'Up':
				if (submenu && isOpen(li)) {
					e.preventDefault();
					const items = getMenuItems(submenu);
					const lastItem = items[items.length - 1];
					if (lastItem) lastItem.focus();
				}
				break;

			case 'ArrowRight':
			case 'Right':
				e.preventDefault();
				if (index < siblings.length - 1) {
					focusItem(siblings[index + 1]);
				} else {
					focusItem(siblings[0]);
				}
				break;

			case 'ArrowLeft':
			case 'Left':
				e.preventDefault();
				if (index > 0) {
					focusItem(siblings[index - 1]);
				} else {
					focusItem(siblings[siblings.length - 1]);
				}
				break;

			case 'Enter':
			case ' ':
				if (
					submenu &&
					e.target ===
						li.querySelector(':scope > .submenu-button, :scope > #language-menu-button')
				) {
					e.preventDefault();
					if (isOpen(li)) {
						closeDropdown(li);
					} else {
						openDropdown(li, 'keyboard');
						const firstItem = getMenuItems(submenu)[0];
						if (firstItem) firstItem.focus();
					}
				}
				break;

			case 'Escape':
			case 'Esc':
				if (isOpen(li)) {
					e.preventDefault();
					closeDropdown(li);
					focusItem(li);
				}
				break;

			case 'Home':
				e.preventDefault();
				focusItem(siblings[0]);
				break;

			case 'End':
				e.preventDefault();
				focusItem(siblings[siblings.length - 1]);
				break;
		}
	};

	/**
	 * Handle keydown within a submenu.
	 * @param {KeyboardEvent} e
	 * @param {HTMLElement} parentLi - The parent top-level li
	 * @param {HTMLElement} submenu
	 */
	const handleSubmenuKeydown = (e, parentLi, submenu) => {
		const items = getMenuItems(submenu);
		const activeElement = document.activeElement;

		// Find current index - check if active element is one of the items or a parent
		let currentIndex = items.indexOf(activeElement);

		// If not found, check if activeElement is a child of one of the items
		if (currentIndex === -1) {
			currentIndex = items.findIndex((item) => item.contains(activeElement));
		}

		switch (e.key) {
			case 'ArrowDown':
			case 'Down':
				e.preventDefault();
				if (currentIndex === -1 || currentIndex >= items.length - 1) {
					items[0]?.focus();
				} else {
					items[currentIndex + 1]?.focus();
				}
				break;

			case 'ArrowUp':
			case 'Up':
				e.preventDefault();
				if (currentIndex === -1 || currentIndex === 0) {
					items[items.length - 1]?.focus();
				} else {
					items[currentIndex - 1]?.focus();
				}
				break;

			case 'Escape':
			case 'Esc':
				e.preventDefault();
				closeDropdown(parentLi);
				focusItem(parentLi);
				break;

			case 'Home':
				e.preventDefault();
				items[0]?.focus();
				break;

			case 'End':
				e.preventDefault();
				items[items.length - 1]?.focus();
				break;
		}
	};

	/** @type {HTMLElement[]} Flat list of all navigable top-level items across the navbar */
	let allNavItems = [];

	/**
	 * Get all top-level navigable items across the entire navbar.
	 * @returns {HTMLElement[]}
	 */
	const getTopLevelItems = () => allNavItems;

	/**
	 * Build the flat list of all navigable top-level items.
	 * Order matches DOM/Tab order: topbar items first, then main menu items.
	 * @param {HTMLElement} navbar
	 */
	const buildNavItemsList = (navbar) => {
		const items = [];

		// Topbar items (custom buttons + language container) — first in DOM
		const topbarContent = navbar.querySelector('.topbar-content');
		if (topbarContent) {
			topbarContent
				.querySelectorAll(':scope > .custom_button, :scope > .language-container')
				.forEach((el) => items.push(el));
		}

		// Main menu items
		const menubar = navbar.querySelector('#main-menu');
		if (menubar) {
			menubar.querySelectorAll(':scope > li.menu-item').forEach((li) => items.push(li));
		}

		allNavItems = items;
	};

	/**
	 * Focus the primary interactive element within a top-level item.
	 * @param {HTMLElement} li
	 */
	const focusItem = (li) => {
		const focusTarget =
			li.querySelector(':scope > a.menu-link') ||
			li.querySelector(':scope > a') ||
			li.querySelector(':scope > #language-menu-button') ||
			li.querySelector(':scope > .submenu-button');

		if (focusTarget) {
			focusTarget.focus();
		}
	};

	/**
	 * Initialize the language switcher dropdown (click only).
	 * Keyboard is handled by initKeyboard called on the container.
	 */
	const initLanguageSwitcher = () => {
		const langButton = document.getElementById('language-menu-button');
		if (!langButton) return;

		const langContainer = langButton.closest('.language-container');
		if (!langContainer) return;

		initClick(langContainer, langButton);
	};

	/**
	 * Initialize all desktop navigation dropdowns.
	 */
	const init = () => {
		const navbar = document.getElementById('navbar-desktop');
		if (!navbar) return;

		// Build the unified list of all navigable items
		buildNavItemsList(navbar);

		// Initialize menu items with submenus (click only)
		const menubar = navbar.querySelector('#main-menu');
		if (menubar) {
			menubar.querySelectorAll(':scope > li.menu-item-has-children').forEach((li) => {
				const button = li.querySelector(':scope > .submenu-button');
				const submenu = li.querySelector(':scope > .submenu-dropdown');

				if (!submenu) return;

				if (button) {
					initClick(li, button);
				}
			});
		}

		// Initialize language switcher (hover + click + submenu keyboard)
		initLanguageSwitcher();

		// Initialize keyboard navigation on ALL navigable items
		allNavItems.forEach((item) => {
			initKeyboard(item);
		});

		// Close dropdowns when clicking outside
		document.addEventListener('click', (e) => {
			if (activeItem && !activeItem.contains(e.target)) {
				closeAll();
			}
		});

		// Close on Escape anywhere
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' || e.key === 'Esc') {
				if (activeItem) {
					const current = activeItem;
					closeAll();
					focusItem(current);
				}
			}
		});
	};

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	// Re-initialize keyboard on cart updates (WooCommerce AJAX cart fragments)
	if (typeof jQuery !== 'undefined') {
		jQuery(document.body).on(
			'wc_fragments_refreshed wc_fragments_loaded updated_wc_div',
			() => {
				if (allNavItems.length > 0) {
					// Re-attach keyboard listeners to cart button
					const cartButton = document.querySelector('.custom_button > a[id*="cart"]');
					if (cartButton) {
						const customButton = cartButton.closest('.custom_button');
						if (customButton && allNavItems.includes(customButton)) {
							initKeyboard(customButton);
						}
					}
				}
			}
		);
	}
})();
